import { useCallback, useEffect, useState } from 'react';
import { canonicalGraph, parseGraphJson } from '@/lib/workflowGraph';
import { syncSpecWithGraph } from '@/lib/workflowSpecSync';

/**
 * Data loading for the workflow builder. Split out of WorkflowDetail so the page is layout
 * and event handling only — the fetch/poll/debounce logic here is what pushed it past the
 * repo's max-lines-per-function and complexity gates.
 */

export const errText = (ex) => (ex.response && ex.response.data && ex.response.data.error) || ex.message;
const asList = (res) => (Array.isArray(res) ? res : res?.data) || [];

/** Statuses worth polling for. Mirrors VoiceCallDetail's LIVE_STATUSES idiom — there is no
 *  SSE/WebSocket anywhere in this app. */
const LIVE_RUN_STATUSES = new Set(['queued', 'running', 'waiting_for_client', 'waiting_for_timer']);

const EMPTY = { form: null, graph: { nodes: [], edges: [] }, fields: [] };

/**
 * The definition plus the agent picker's options, and the pristine copies dirty tracking
 * compares against.
 *
 * There is no `isNew` branch: workflows are created by the dialog on the list page, so by
 * the time this page mounts the row always exists. `reloadKey` lets a save elsewhere
 * (the LLM App spec panel) re-run the load — a spec save recompiles the graph server-side,
 * so the canvas must refetch to show it.
 */
export function useWorkflowData({ id, fetchApi, onError, reloadKey = 0 }) {
  const [state, setState] = useState(EMPTY);
  const [pristine, setPristine] = useState(EMPTY);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchApi('crud', { model: 'AppWorkflow', action: 'show', where: { id } }),
      fetchApi('crud', { model: 'AppAiAgent', action: 'list', limit: 200 }).catch(() => ({ data: [] })), // agents.view.org may not be granted
    ])
      .then(([wfRes, agentsRes]) => {
        setAgents(asList(agentsRes));
        const wf = wfRes && wfRes.data ? wfRes.data : wfRes;
        let fields = [];
        try { fields = JSON.parse(wf.input_schema || '[]'); } catch { fields = []; }
        const loaded = {
          form: { ...wf, agent_key: wf.agent_key || '', application_id: wf.application_id || null },
          graph: parseGraphJson(wf.graph_json),
          fields: Array.isArray(fields) ? fields : [],
        };
        setState(loaded);
        setPristine(loaded);
      })
      .catch((ex) => onError(errText(ex)))
      .finally(() => setLoading(false));
  }, [id, reloadKey]);

  return { state, setState, pristine, setPristine, agents, loading };
}

/**
 * The palette's raw ingredients. Every call degrades to empty on failure: a viewer without
 * connections.view.org still gets a working palette, just without the connection presets —
 * the same defensive pattern AgentDetail uses for its four catalog fetches.
 *
 * Tool options are refetched when the agent changes, because that is exactly what decides
 * which tools a `tool` node can resolve.
 */
/** One provider's model list (GET /api/agent/models) as a `[key, ids]` pair. A failed
 *  fetch resolves to an empty list rather than rejecting: the palette falls back to
 *  MODEL_OPTIONS for that provider, and one dead provider must not blank the others. */
function modelIdsFor(fetchApi, provider) {
  return fetchApi('agentModels', { provider })
    .then((res) => [provider, (res?.models || []).map((m) => m.id)])
    .catch(() => [provider, []]);
}

export function useBuilderCatalogs({ fetchApi, agentKey }) {
  const [nodeCatalog, setNodeCatalog] = useState([]);
  const [transformCatalog, setTransformCatalog] = useState([]);
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [toolOptions, setToolOptions] = useState([]);
  // The company's other workflows, for the Sub-workflow / For Each pickers. Keyed by `key`
  // downstream, so only these three columns are needed.
  const [workflows, setWorkflows] = useState([]);
  const [modelsByProvider, setModelsByProvider] = useState({});
  const [connectors, setConnectors] = useState([]);

  const loadProviderModels = (list) => Promise
    .all(list.filter((p) => p.configured).map((p) => modelIdsFor(fetchApi, p.key)))
    .then((pairs) => setModelsByProvider(Object.fromEntries(pairs)));

  useEffect(() => {
    fetchApi('workflowNodeCatalog').then((res) => setNodeCatalog(Array.isArray(res) ? res : [])).catch(() => {});
    fetchApi('workflowTransformCatalog').then((res) => setTransformCatalog(Array.isArray(res) ? res : [])).catch(() => {});
    fetchApi('agentProviders')
      .then((res) => {
        const list = res?.providers || [];
        setProviders(list);
        return loadProviderModels(list);
      })
      .catch(() => {});
    fetchApi('crud', { model: 'AppConnection', action: 'list', limit: 200 })
      .then((res) => setConnections(asList(res)))
      .catch(() => {});
    fetchApi('crud', {
      model: 'AppWorkflow', action: 'list', limit: 200, select: ['id', 'key', 'name', 'is_active'],
    })
      .then((res) => setWorkflows(asList(res).filter((w) => w.is_active !== false && w.is_active !== 0)))
      .catch(() => {});
    fetchApi('workflowConnectors')
      .then((res) => setConnectors(res?.connectors || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchApi('workflowToolOptions', agentKey || '')
      .then((res) => setToolOptions(res?.tools || []))
      .catch(() => setToolOptions([]));
  }, [agentKey]);

  const aiConnections = connections.filter(
    (c) => c.category_key === 'ai-model' && c.is_active !== false && c.is_active !== 0,
  );
  // Image-provider connections for the Generate Image node — same active-only rule as
  // aiConnections, but its own category so an LLM key is never offered as an image key.
  const imageConnections = connections.filter(
    (c) => c.category_key === 'ai-image' && c.is_active !== false && c.is_active !== 0,
  );
  return {
    nodeCatalog, transformCatalog, providers, connections, aiConnections, imageConnections,
    toolOptions, modelsByProvider, workflows, connectors,
  };
}

/**
 * Live lint against POST /api/workflows/validate — the same validateGraph the model hook and
 * the engine run, so the editor shows the error the save would produce instead of the user
 * discovering it on Save.
 *
 * Debounced because it fires on every graph edit, and the graph object changes on each drag
 * frame. 500ms is long enough that a drag produces one call, not forty.
 */
export function useGraphValidation({ fetchApi, graph, enabled }) {
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    // An empty graph is the not-yet-loaded artifact, never a lintable state: the server's
    // only answer to {nodes:[]} is the uninformative "needs at least one node" 400, so
    // sending it just paints a bogus banner on every page open. A genuinely emptied
    // canvas is still enforced at Save (server-side validateGraph). Neutral null, not an
    // error — the banner must not claim a problem that isn't one.
    if (!enabled || !graph.nodes.length) return undefined;
    setValidating(true);
    const timer = setTimeout(() => {
      fetchApi('workflowValidate', { graph })
        .then((res) => setValidation(res))
        // A 400 IS the answer here, not a failure — the endpoint returns {ok:false,error}
        // with that status, and axios rejects on it.
        .catch((ex) => setValidation(ex.response?.data?.error
          ? { ok: false, error: ex.response.data.error }
          : { ok: false, error: errText(ex) }))
        .finally(() => setValidating(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [enabled, graph]);

  return { validation, validating };
}

/**
 * What Workflow Architect is told about the screen, sent with every turn from the dock
 * (`context` on POST /api/agent/chat). Without it the agent has only a message string and
 * no idea which of the company's workflows "add a step here" refers to.
 *
 * The graph is the CANONICAL one — the same reactflow-noise-free shape the Specification
 * panel shows and the server stores — so what the model reads is what would be saved.
 * Deliberately compact: it competes for the same prompt budget as history and retrieval.
 */
/** Longest string sent verbatim. Above this a value is previewed, never dropped. */
const MAX_CONTEXT_FIELD_CHARS = 200;

/**
 * Shorten the long free text inside a graph so the SHAPE of the graph always fits.
 *
 * The whole payload is clamped server-side at 4000 characters
 * (services/chat/context/prompt.js), and a graph's size is dominated by a handful of
 * fields — one `llm` node's `system_prompt` plus `output_schema` came to 13,958 of the
 * prescription workflow's 15,250 characters. Sent verbatim, the cut landed inside that
 * first node and the architect never saw the other three nodes or a single edge, so it
 * reported a four-node graph as a one-node graph. Node ids, types and every edge are
 * what it reasons over; prompt prose is what it reads only if it is rewriting that node.
 * So abridge the prose and the structure always survives.
 */
function abridgeLongStrings(value) {
  if (typeof value === 'string') {
    if (value.length <= MAX_CONTEXT_FIELD_CHARS) return value;
    return `…abridged, ${value.length} chars: ${value.slice(0, MAX_CONTEXT_FIELD_CHARS)}`;
  }
  if (Array.isArray(value)) return value.map(abridgeLongStrings);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, abridgeLongStrings(v)]));
  }
  return value;
}

/**
 * What Workflow Architect is told about the screen, sent with every turn from the dock
 * (`context` on POST /api/agent/chat). Without it the agent has only a message string and
 * no idea which of the company's workflows "add a step here" refers to.
 *
 * The graph is the CANONICAL one — the same reactflow-noise-free shape the Specification
 * panel shows and the server stores — so what the model reads is what would be saved.
 * Deliberately compact: it competes for the same prompt budget as history and retrieval.
 */
export function buildWorkflowContext({ form, graph, fields, toolOptions }) {
  if (!form) return null;
  const canonical = canonicalGraph(graph.nodes, graph.edges);
  return JSON.stringify({
    screen: 'workflow builder',
    // Load-bearing, not a courtesy: an abridged value pasted back into a proposed graph
    // would overwrite a real 7000-character prompt with its own 200-character preview,
    // and propose_workflow_graph replaces the canvas wholesale rather than merging.
    note: `Long text fields are abridged and marked "…abridged, N chars". An abridged value is a PREVIEW, not the content. Never copy one into a graph you propose — call get_workflow first to read the field in full.`,
    workflow: {
      key: form.key,
      name: form.name,
      description: form.description || null,
      agent_key: form.agent_key || null,
      version: form.version,
      is_active: !!form.is_active,
    },
    input_schema: abridgeLongStrings(fields || []),
    graph: {
      // Canvas coordinates are dropped: the architect never reasons about them and the
      // server re-layouts anything it proposes.
      nodes: (canonical.nodes || []).map(({ position, ...node }) => abridgeLongStrings(node)),
      edges: canonical.edges || [],
    },
    // The only tool names a `tool` node in THIS workflow can legally resolve
    // (routes/workflowsRouter.js /tool-options — agent-scoped and auto-only).
    available_tool_names: (toolOptions || []).map((t) => t.name),
  }, null, 0);
}

/** The row a save writes. `key` is never included — it is immutable after create.
 *  `spec_json` rides along when the workflow has one — with the canvas intelligence node's
 *  config synced into it first (lib/workflowSpecSync.js): the server recompiles the graph
 *  from the spec (models/AppWorkflow.model.js's _applySpec), so an unsynced spec would
 *  silently discard every model/connection/output edit made on the canvas. */
function workflowPayload(form, graph, inputFields) {
  const data = {
    name: form.name,
    description: form.description || null,
    agent_key: form.agent_key || null,
    application_id: form.application_id || null,
    is_active: !!form.is_active,
    graph_json: JSON.stringify(canonicalGraph(graph.nodes, graph.edges)),
    input_schema: JSON.stringify(inputFields.filter((f) => f.name.trim())),
  };
  if (form.spec_json) data.spec_json = syncSpecWithGraph(form.spec_json, graph);
  return data;
}

/**
 * Save, publish and run — the three writes, plus the pristine bookkeeping each one owes.
 *
 * `version` is echoed forward locally because models/AppWorkflow.model.js bumps it in a
 * beforeUpdate hook and korm's update response does not return the row; refetching just to
 * read one integer back would cost a round trip on every save.
 */
export function useWorkflowActions({ id, fetchApi, state, setState, setPristine, setErr, setNotice }) {
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  /** Commits `form` locally as the new pristine baseline, optionally with the graph too. */
  const commit = (form, withGraph) => {
    setState((s) => ({ ...s, form }));
    setPristine((p) => (withGraph
      ? { form, graph: canonicalGraph(state.graph.nodes, state.graph.edges), fields: state.fields }
      : { ...p, form }));
  };

  const save = async () => {
    setSaving(true);
    setErr('');
    setNotice('');
    try {
      const data = workflowPayload(state.form, state.graph, state.fields);
      await fetchApi('crud', { model: 'AppWorkflow', action: 'update', where: { id }, data });
      // spec_json is echoed forward too: the synced spec (not the pre-save copy) is now
      // what the LLM App panel shows and the next save starts from.
      commit({
        ...state.form,
        version: Number(state.form.version || 1) + 1,
        ...(data.spec_json ? { spec_json: data.spec_json } : {}),
      }, true);
      setNotice('Saved.');
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async () => {
    setPublishing(true);
    setErr('');
    try {
      const is_active = !state.form.is_active;
      await fetchApi('crud', { model: 'AppWorkflow', action: 'update', where: { id }, data: { is_active } });
      commit({ ...state.form, is_active, version: Number(state.form.version || 1) + 1 }, false);
      setNotice(is_active ? 'Published — agents can call this workflow.' : 'Unpublished.');
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setPublishing(false);
    }
  };

  return { saving, publishing, save, togglePublished };
}

/** Runs list + the open run's steps, polled only while something is actually live and only
 *  while the Runs tab is showing — the same gated-interval idiom as VoiceCallDetail. */
export function useWorkflowRuns({ id, tab, fetchApi, onError }) {
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadRuns = useCallback(() => {
    setLoading(true);
    return fetchApi('workflowRuns', id)
      .then((rows) => setRuns(Array.isArray(rows) ? rows : []))
      .catch(() => {}) // a transient list failure must not blow away the open run
      .finally(() => setLoading(false));
  }, [id]);

  const openRun = useCallback((runId) => fetchApi('workflowRunShow', runId)
    .then((res) => { setSelectedRun(res.run); setSteps(res.steps || []); })
    .catch((ex) => onError(errText(ex))), []);

  useEffect(() => { if (tab === 'runs') loadRuns(); }, [tab, loadRuns]);

  useEffect(() => {
    if (tab !== 'runs') return undefined;
    const live = (selectedRun && LIVE_RUN_STATUSES.has(selectedRun.status))
      || runs.some((r) => LIVE_RUN_STATUSES.has(r.status));
    if (!live) return undefined;
    const timer = setInterval(() => {
      loadRuns();
      if (selectedRun) openRun(selectedRun.id);
    }, 1500);
    return () => clearInterval(timer);
  }, [tab, runs, selectedRun, loadRuns, openRun]);

  return { runs, selectedRun, steps, loading, loadRuns, openRun };
}
